/**
 * Member-portal appearance & timezone preferences.
 *
 * The member portal has no AuthContext (that's staff-only), so this module is
 * the member-side equivalent of AuthContext.applyTheme + the staff timezone
 * preference: it reads/writes the member's `theme` and `timezone` from
 * `GET/PUT /api/member-portal/settings` (via the member auth + X-CBO-ID
 * `memberApiFetch` pattern) and applies the theme by toggling the `dark` class
 * on <html> — the exact mechanism PublicThemeToggle and the index.html boot
 * script already use, so the whole app's `dark:` Tailwind classes light up.
 *
 * `theme` is persisted to BOTH localStorage('theme') (instant paint, shared with
 * the boot script) and the server (cross-device). `timezone` is cached in
 * localStorage + a module var so date formatting is synchronous.
 */
import { memberApiFetch } from './memberAuth';

export type MemberTheme = 'light' | 'dark' | 'system';

export interface MemberPreferences {
  theme: MemberTheme;
  timezone: string;
}

/** Shared with PublicThemeToggle + the index.html boot script. */
const THEME_KEY = 'theme';
const TZ_KEY = 'member_timezone';

let cachedTimezone: string | null = null;
let prefsPromise: Promise<MemberPreferences | null> | null = null;

/** The browser's IANA timezone, with a safe fallback. */
export function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles';
  } catch {
    return 'America/Los_Angeles';
  }
}

export function getStoredTheme(): MemberTheme | null {
  try {
    const v = localStorage.getItem(THEME_KEY);
    if (v === 'light' || v === 'dark' || v === 'system') return v;
  } catch {
    /* ignore */
  }
  return null;
}

export function getStoredTimezone(): string | null {
  if (cachedTimezone) return cachedTimezone;
  try {
    const v = localStorage.getItem(TZ_KEY);
    if (v) {
      cachedTimezone = v;
      return v;
    }
  } catch {
    /* ignore */
  }
  return null;
}

/**
 * Apply a theme by toggling the `dark` class on <html>.
 * 'system' follows the OS `prefers-color-scheme`.
 */
export function applyTheme(theme: MemberTheme): void {
  if (typeof document === 'undefined') return;
  const prefersDark =
    typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-color-scheme: dark)').matches;
  const isDark = theme === 'dark' || (theme === 'system' && prefersDark);
  document.documentElement.classList.toggle('dark', isDark);
}

export function persistThemeLocal(theme: MemberTheme): void {
  try {
    localStorage.setItem(THEME_KEY, theme);
  } catch {
    /* ignore */
  }
}

export function persistTimezoneLocal(tz: string): void {
  cachedTimezone = tz;
  try {
    localStorage.setItem(TZ_KEY, tz);
  } catch {
    /* ignore */
  }
}

/** GET /api/member-portal/settings and normalize the theme + timezone fields. */
async function fetchMemberPreferences(): Promise<MemberPreferences | null> {
  try {
    const res = await memberApiFetch('/settings');
    if (!res.ok) return null;
    const data = await res.json();
    const theme: MemberTheme =
      data?.theme === 'dark' || data?.theme === 'light' || data?.theme === 'system'
        ? data.theme
        : 'system';
    const timezone: string =
      typeof data?.timezone === 'string' && data.timezone ? data.timezone : browserTimezone();
    // Warm the caches so later synchronous reads are correct.
    persistTimezoneLocal(timezone);
    return { theme, timezone };
  } catch {
    return null;
  }
}

/**
 * Load member preferences once per session (deduped). The member layout and the
 * useMemberTimezone hook both call this, but only one network request is made.
 * Pass force=true to re-fetch after an update.
 */
export function loadMemberPreferences(force = false): Promise<MemberPreferences | null> {
  if (!prefsPromise || force) prefsPromise = fetchMemberPreferences();
  return prefsPromise;
}

/**
 * PUT a partial preferences update. The backend COALESCEs missing fields, so
 * sending only { theme } or only { timezone } won't clobber the other.
 */
export async function updateMemberPreferences(partial: Partial<MemberPreferences>): Promise<void> {
  const res = await memberApiFetch('/settings', {
    method: 'PUT',
    body: JSON.stringify(partial),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: 'Failed to update preferences' }));
    throw new Error(err.detail || 'Failed to update preferences');
  }
  // Reflect the change in the deduped cache for subsequent reads.
  if (partial.timezone) persistTimezoneLocal(partial.timezone);
  prefsPromise = null;
}
